import { getBuildInfo } from '@confluence/build-info';
import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { isBranchDeployFn } from '@confluence/atlassian-internal-help-items';
import { getMonitoringClient } from '@confluence/monitoring';

const FORCE_UPDATE_FILE_NAME = 'force-update.json';
const FORCE_UPDATE_FETCH_TIMEOUT = 1000;

type ForceUpdateResponseType = { forceUpdateTimestamp: number | null };

function hasBranchDeployOverride() {
	try {
		return Boolean(window.sessionStorage.getItem('branch-deploy.force-update.override'));
	} catch (_) {
		// noop
	}
	return false;
}

function rejectOnTimeout(promise: Promise<ForceUpdateResponseType>, timeoutMilliseconds) {
	return new Promise<ForceUpdateResponseType>((resolve, reject) => {
		// Cypress intercepted request is very slow. No time out when running the test
		const timeoutId = window['Cypress'] ? null : setTimeout(reject, timeoutMilliseconds);
		promise.then(resolve, reject).finally(() => {
			if (timeoutId) clearTimeout(timeoutId);
		});
	});
}

function reportAnalyticsAction(action, attributes) {
	getAnalyticsWebClient().then(
		(analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'confluence-frontend',
				actionSubject: 'forceUpdateSPA',
				action,
				attributes,
			});
		},
		() => {},
	);
}

async function isUpdateReleased() {
	const path = getBuildInfo().PUBLIC_PATH + FORCE_UPDATE_FILE_NAME;
	const buildDate = getBuildInfo().BUILD_DATE;
	const navigationStart = window.performance.timing.navigationStart;

	if (
		typeof buildDate !== 'number' ||
		buildDate === 0 ||
		typeof navigationStart !== 'number' ||
		navigationStart === 0
	) {
		// since we'll be checking against both build date and navigation timings, we REALLY need them to be meaningful
		return false;
	}

	const response = await rejectOnTimeout(
		cfetch(path, {
			mode: 'cors',
			// The value of the 'Access-Control-Allow-Origin' header in the response must not be the wildcard '*'
			// when the request's credentials mode is 'include'.
			credentials: 'omit',
			headers: {
				'x-bifrost-version-override': 'force-update',
			},
		}).then((request) => request.json()),
		FORCE_UPDATE_FETCH_TIMEOUT,
	).catch(() => {
		// this block is handling network error or a rejection due to timeout.
		return { forceUpdateTimestamp: null };
	});

	const forceUpdateTimestamp = Number(response.forceUpdateTimestamp);
	if (isNaN(forceUpdateTimestamp)) {
		return false;
	}

	// Check if update is available
	if (forceUpdateTimestamp > buildDate || forceUpdateTimestamp > navigationStart) {
		// force update timestamp indicates that either
		// a) the build user has currently loaded is "outdated", or
		// b) the user loaded the page at a time when configuration (e.g. feature flags) might have been wrong

		reportAnalyticsAction('triggered', {
			navigationStart,
			buildDate,
			forceUpdateTimestamp,
		});

		return true;
	}

	reportAnalyticsAction('checked', {
		navigationStart,
		buildDate,
		forceUpdateTimestamp,
	});
	return false;
}

export async function forceUpdateCheck() {
	try {
		const { featureFlags } = await getSessionData();

		if (isBranchDeployFn(featureFlags) && !hasBranchDeployOverride()) {
			// by default, ignore for branch deploys to allow developers test old versions no matter what the `force-update` timestamp is.
			// Yet, continue with the check if the developer specifies an explicit override. This will allow to test forceUpdate logic at pull request stage.
			return false;
		}

		// we are having an `await` here to ensure that any errors coming from `isUpdateReleased` function
		// will get caught here, as opposed to propagating this error down to where `forceUpdateCheck` is invoked.
		return await isUpdateReleased();
	} catch (error) {
		getMonitoringClient().submitError(error, { attribution: 'backbone' });

		return false;
	}
}
